import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { login as apiLogin, logout as apiLogout, onSessionExpired } from '../lib/auth';
import { apiGet, isTransientError, retryDelayMs, transientMessage } from '../lib/api';
import type { CurrentUser } from '../types';
import { featureEnabled, type FeatureName } from '../lib/features';
import { applyServerPreferences } from '../lib/preferences';

interface AuthCtx {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;
  /**
   * Message temporaire quand /api/me/full n'a pas pu être lu pour une raison
   * passagère (429, 5xx, réseau). La session n'est PAS considérée comme perdue :
   * un nouvel essai est programmé (Retry-After s'il est fourni).
   */
  unavailable: string | null;
  /**
   * La session ouverte vient d'expirer (rafraîchissement refusé) : la page
   * protégée renvoie vers /login, qui l'explique. Remis à faux à la connexion.
   */
  sessionExpired: boolean;
  login: (mail: string, pwd: string, mfa?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const userRef = useRef<CurrentUser | null>(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRetry = () => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
    retryTimer.current = null;
  };

  const refresh = useCallback(async (): Promise<void> => {
    clearRetry();
    try {
      const me = await apiGet<CurrentUser>('/api/me/full');
      // Préférences du compte (taille, thème) : elles priment sur la copie
      // locale posée avant le rendu (autre appareil, autre compte).
      applyServerPreferences(me.preferences);
      userRef.current = me;
      setUser(me);
      setUnavailable(null);
    } catch (e) {
      if (isTransientError(e)) {
        // Limite de débit, service indisponible ou réseau : on ne déconnecte
        // pas. L'utilisateur déjà chargé reste en place ; on réessaie plus tard.
        setUnavailable(transientMessage(e));
        retryTimer.current = setTimeout(() => void refresh(), retryDelayMs(e));
      } else {
        // 401 après échec du refresh (session finie), ou 403/404 sur
        // /api/me/full (compte plus utilisable) : seuls cas qui renvoient vers
        // /login.
        setUser(null);
        setUnavailable(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => clearRetry, []);

  // Fin de session constatée par n'importe quel appel (401 + rafraîchissement
  // refusé) : on purge l'utilisateur, la garde des pages protégées renvoie vers
  // /login avec l'adresse demandée. Sans utilisateur chargé (page publique,
  // /login, /aide), rien ne change : aucune redirection.
  useEffect(
    () =>
      onSessionExpired(() => {
        if (!userRef.current) return;
        userRef.current = null;
        clearRetry();
        setUser(null);
        setUnavailable(null);
        setSessionExpired(true);
      }),
    [],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (mail: string, pwd: string, mfa?: string) => {
      setError(null);
      try {
        await apiLogin(mail, pwd, mfa);
        setSessionExpired(false);
        await refresh();
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Erreur de connexion';
        setError(msg);
        throw e;
      }
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    clearRetry();
    await apiLogout();
    setUser(null);
    setUnavailable(null);
    setSessionExpired(false);
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, error, unavailable, sessionExpired, login, logout, refresh }}>{children}</Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}

/** Interrupteur de fonction pour l'utilisateur connecté (fermé si inconnu). */
export function useFeature(name: FeatureName): boolean {
  const { user } = useAuth();
  return featureEnabled(user, name);
}
